// Project state store
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  projectsApi,
  type Project,
  type ProjectStats,
  type AccountSummary,
  type ProjectMetadataPatch,
} from '@/lib/api/client'

interface CreateProjectInput {
  name: string
  slug: string
  description?: string
  companyType?: Project['companyType']
  productType?: Project['productType']
  market?: Project['market']
  industry?: string | null
  clientName?: string | null
  tags?: string[]
}

interface ProjectState {
  projects: Project[]
  currentProject: Project | null
  stats: Record<string, ProjectStats>
  summary: AccountSummary | null
  isLoading: boolean
  error: string | null

  loadProjects: (accountId: string) => Promise<void>
  loadPortfolio: (accountId: string) => Promise<void>
  selectProject: (accountId: string, projectId: string) => Promise<void>
  createProject: (accountId: string, input: CreateProjectInput) => Promise<Project>
  updateProject: (accountId: string, projectId: string, updates: ProjectMetadataPatch) => Promise<void>
  deleteProject: (accountId: string, projectId: string) => Promise<void>
  clearCurrentProject: () => void
  clearError: () => void
  clearProjects: () => void
}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      projects: [],
      currentProject: null,
      stats: {},
      summary: null,
      isLoading: false,
      error: null,

      loadProjects: async (accountId: string) => {
        set({ isLoading: true, error: null })
        try {
          const projects = await projectsApi.list(accountId)
          set({ projects, isLoading: false })

          const current = get().currentProject
          if (current) {
            const updated = projects.find(p => p.id === current.id)
            if (updated) set({ currentProject: updated })
            else set({ currentProject: null })
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load projects'
          set({ error: message, isLoading: false })
        }
      },

      loadPortfolio: async (accountId: string) => {
        set({ isLoading: true, error: null })
        try {
          const { projects, stats, summary } = await projectsApi.portfolio(accountId)
          set({ projects, stats, summary, isLoading: false })

          const current = get().currentProject
          if (current) {
            const updated = projects.find(p => p.id === current.id)
            set({ currentProject: updated ?? null })
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load portfolio'
          set({ error: message, isLoading: false })
        }
      },

      selectProject: async (accountId: string, projectId: string) => {
        set({ isLoading: true, error: null })
        try {
          const project = await projectsApi.get(accountId, projectId)
          set({ currentProject: project, isLoading: false })
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load project'
          set({ error: message, isLoading: false })
          throw err
        }
      },

      createProject: async (accountId: string, input: CreateProjectInput) => {
        set({ isLoading: true, error: null })
        try {
          const project = await projectsApi.create(accountId, input)
          set(state => ({
            projects: [...state.projects, project],
            currentProject: project,
            isLoading: false,
          }))
          return project
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to create project'
          set({ error: message, isLoading: false })
          throw err
        }
      },

      updateProject: async (accountId: string, projectId: string, updates: ProjectMetadataPatch) => {
        set({ isLoading: true, error: null })
        try {
          const project = await projectsApi.update(accountId, projectId, updates)
          set(state => ({
            projects: state.projects.map(p => p.id === projectId ? project : p),
            currentProject: state.currentProject?.id === projectId ? project : state.currentProject,
            isLoading: false,
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to update project'
          set({ error: message, isLoading: false })
          throw err
        }
      },

      deleteProject: async (accountId: string, projectId: string) => {
        set({ isLoading: true, error: null })
        try {
          await projectsApi.delete(accountId, projectId)
          set(state => ({
            projects: state.projects.filter(p => p.id !== projectId),
            currentProject: state.currentProject?.id === projectId ? null : state.currentProject,
            isLoading: false,
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to delete project'
          set({ error: message, isLoading: false })
          throw err
        }
      },

      clearCurrentProject: () => set({ currentProject: null }),

      clearError: () => set({ error: null }),

      clearProjects: () => set({ projects: [], currentProject: null, stats: {}, summary: null }),
    }),
    {
      name: 'ispot-project-v3',
      partialize: (state) => ({
        // Only persist current project selection
        currentProject: state.currentProject,
      }),
    }
  )
)
